/**
 * OctopusMaterial — the arm pose evaluator plus this game's underwater lighting.
 *
 * The vertex stage is the interesting half and it is not ours: world/octopus/voxelOctopus.js
 * ships `OCTO_ARM_GLSL`, a closed-form arm curve WITH its analytic derivative, so a voxel can
 * be placed and framed entirely per-vertex. Eight arms of eleven thousand voxels animate in
 * one draw call with nothing rebuilt on the CPU.
 *
 * The fragment stage IS ours, and has to be: the donor ran a plain Lambert with its own
 * caustics, which would put the animal in a different ocean from everything else on screen.
 * This is the same half-lambert key, hemispheric ambient, fresnel rim, dapple, exp2 fog and
 * shadow lookup the sharks and the reef wear.
 */
import * as THREE from 'three';
import { OCEAN, WATER } from '../data/tuning.ts';
import { COAST_GLSL } from '../world/Coast.ts';
import { OCTO_ARM_GLSL } from '../world/octopus/voxelOctopus.js';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';

function srgb(hex: number): THREE.Color {
  return new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
}

const VERT = /* glsl */ `
${OCTO_ARM_GLSL}
${COAST_GLSL}

uniform float uVox;
uniform float uDuneAmp;
uniform float uReefRise;
uniform float uGround;    // half a world-space voxel: how high a cube centre sits off the bed

attribute vec3 aRest;

varying vec3  vWorldPos;
varying vec3  vWorldNormal;
varying vec3  vSkin;
varying float vRole;
varying float vShade;

void main() {
  vec3 centre;
  if (aArm < 0.0) {
    /* Body. The mantle breathes about its own centre and squeezes hard on a jet — that
       contraction is what actually propels an octopus, so it is driven by the same pulse
       that drives the thrust rather than being a separate animation. */
    float br = 1.0 + uBreath * 0.055 * sin(uTime * 1.15) - uJet * 0.16 + uOpen * 0.10;
    centre = vec3(aRest.x * br,
                  uMantleY + (aRest.y - uMantleY) * (1.0 + uBreath * 0.045 * sin(uTime * 1.15 + 0.6)),
                  aRest.z * br);
    /* The mantle is only ~0.7 units across, so raw body position spans barely one noise cell
       and the sac comes out flat. Scale the skin coordinate so it carries the same blotch
       density as the arms. */
    vSkin = aRest * 3.2;
  } else {
    vec3 P, T;
    armPoint(aArm, aT, P, T);
    /* Frame carried from the arm's OWN base direction. A cross(up, T) frame collapses the
       instant an arm points straight down, which is precisely when a curling octopus needs it. */
    float th = aArm * 0.7853981634;
    vec3 ref = vec3(-sin(th), 0.0, cos(th));
    vec3 N = normalize(ref - T * dot(ref, T));
    vec3 B = cross(T, N);
    centre = P + N * aOff.x + B * aOff.y;
    /* Skin coordinate is the REST parameterisation, so the pattern is painted on the animal
       and flows with it instead of the animal swimming through a pattern. */
    vSkin = vec3(aOff.x * 3.0, aT * 2.4 - 1.0, aArm * 0.37);
  }

  /* DRAPE. An arm reaches nearly three units from the body in whatever direction the pose
     points, so no clamp on the BODY can keep the tips out of the seabed — and an octopus that
     sinks its arms into rock is the one thing that breaks the illusion instantly.

     The bed is an analytic field, so the vertex stage can simply ask it. Each voxel CENTRE is
     tested and the whole cube lifted by the shortfall: clamping per-vertex instead would
     squash boxes into wedges against the ground, and the cube silhouette is the entire look.

     What this buys is not just the absence of clipping — it is conformance. Arms flatten and
     spread over whatever they cross, which is exactly what the animal does. */
  vec4 cw = modelMatrix * vec4(centre, 1.0);
  float lift = max(0.0, bedY(cw.xz, uDuneAmp, uReefRise) + uGround - cw.y);

  // Voxels stay axis-aligned: a cube chain reads as a cube chain, and orienting each box to
  // the arm frame would smooth the silhouette into a tube, which is the opposite of the point.
  vec4 world = modelMatrix * vec4(position * uVox + centre, 1.0);
  world.y += lift;
  vWorldPos = world.xyz;
  vWorldNormal = normalize(mat3(modelMatrix) * normal);
  vRole = aRole;
  vShade = aShade;

  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const FRAG = /* glsl */ `
uniform vec3  uSunDir;
uniform vec3  uSunColor;
uniform vec3  uAmbientTop;
uniform vec3  uAmbientBottom;
uniform vec3  uRimColor;
uniform vec3  uFogColor;
uniform float uFogDensity;
uniform float uTime;
uniform float uChromT;
uniform float uContrast;
uniform int   uSkinMode;
uniform vec3  uPig;
uniform vec3  uPig2;
uniform sampler2D uShadowMap;
uniform mat4      uShadowMatrix;
uniform float     uShadowStrength;

varying vec3  vWorldPos;
varying vec3  vWorldNormal;
varying vec3  vSkin;
varying float vRole;
varying float vShade;

float h31(vec3 p){ p = fract(p * 0.3183099 + vec3(0.71, 0.113, 0.419)); p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
float n31(vec3 p){ vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(h31(i), h31(i+vec3(1,0,0)), f.x), mix(h31(i+vec3(0,1,0)), h31(i+vec3(1,1,0)), f.x), f.y),
             mix(mix(h31(i+vec3(0,0,1)), h31(i+vec3(1,0,1)), f.x), mix(h31(i+vec3(0,1,1)), h31(i+vec3(1,1,1)), f.x), f.y), f.z); }
float fb31(vec3 p){ return n31(p) * 0.55 + n31(p * 2.03) * 0.28 + n31(p * 4.1) * 0.17; }

float lattice(vec2 p) {
  return (sin(p.x) + sin(p.y * 0.87 + 1.7) + sin((p.x + p.y) * 0.62 + 3.1)) * 0.3333333;
}
float dapple(vec2 w, float t) {
  float a = 1.0 - abs(lattice(w * 0.55 + vec2( 0.31, -0.22) * t));
  float b = 1.0 - abs(lattice(w * 1.27 + vec2(-0.19,  0.42) * t));
  return pow(clamp(min(a, b), 0.0, 1.0), 5.0);
}

float shadowAt(vec3 world, vec3 n, float ndl) {
  vec4 lp = uShadowMatrix * vec4(world + n * 0.12, 1.0);
  vec3 c = lp.xyz / lp.w;
  c = c * 0.5 + 0.5;
  if (c.x < 0.0 || c.x > 1.0 || c.y < 0.0 || c.y > 1.0 || c.z > 1.0) return 1.0;
  float bias = mix(0.0018, 0.0004, clamp(ndl, 0.0, 1.0));
  float texel = 1.0 / 2048.0;
  float lit = 0.0;
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2(-texel, -texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2( texel, -texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2(-texel,  texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2( texel,  texel)).r);
  lit *= 0.25;
  vec2 e = abs(c.xy - 0.5) * 2.0;
  return mix(1.0, lit, 1.0 - smoothstep(0.80, 1.0, max(e.x, e.y)));
}

void main() {
  vec3 N = normalize(vWorldNormal);
  vec3 V = normalize(cameraPosition - vWorldPos);
  vec3 L = normalize(uSunDir);

  /* Pigment by role. The eye reads as a dark horizontal pupil with a sliver of iris in a dark
     lid — not a bright disc; a filled iris at this voxel count is just a coloured square. */
  vec3 base;
  if (vRole > 4.5)                          base = vec3(0.045, 0.040, 0.058);   // lid
  else if (vRole > 3.5)                     base = uPig;                        // mantle
  else if (vRole > 2.5)                     base = mix(vec3(0.56, 0.49, 0.31), uPig2, 0.34); // iris
  else if (vRole > 1.5)                     base = vec3(0.022, 0.020, 0.030);   // pupil
  else if (vRole > 0.5)                     base = uPig2 * 1.18;                // sucker
  else                                      base = uPig;                        // skin

  if (vRole < 0.5 || vRole > 3.5) {
    /* Chromatophore display, as a function of the baked skin coordinate — so the pattern is
       painted on the animal and flows over it as the arms move, rather than the animal
       swimming through a fixed field. */
    float d = 0.0;
    if (uSkinMode == 1)      d = fb31(vSkin * 2.6 + vec3(0.0, uChromT * 0.35, 0.0));
    else if (uSkinMode == 2) d = 0.5 + 0.5 * sin(vSkin.y * 3.4 - uChromT * 2.4 + fb31(vSkin * 1.7) * 4.0);
    else if (uSkinMode == 3) d = 0.5 + 0.5 * sin(length(vSkin.xz) * 7.0 - uChromT * 3.0);
    if (uSkinMode > 0) base = mix(base, mix(uPig * 0.42, uPig2, smoothstep(0.42, 0.78, d)), uContrast);
  }
  base *= vShade;

  float ndl = dot(N, L) * 0.5 + 0.5;
  float shade = mix(1.0, shadowAt(vWorldPos, N, dot(N, L)), uShadowStrength);
  vec3 key = uSunColor * (ndl * ndl) * shade;
  vec3 ambient = mix(uAmbientBottom, uAmbientTop, N.y * 0.5 + 0.5);
  vec3 col = base * (ambient + key);

  float up = smoothstep(0.12, 0.8, N.y);
  col += mix(base, vec3(1.0), 0.35) * uSunColor * (dapple(vWorldPos.xz, uTime) * up * 1.1 * shade);

  float fres = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 3.0);
  col += uRimColor * (fres * 0.85);

  float fd = uFogDensity * length(cameraPosition - vWorldPos);
  col = mix(col, uFogColor, clamp(1.0 - exp(-fd * fd), 0.0, 1.0));

  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export class OctopusMaterial extends THREE.ShaderMaterial {
  /** @param worldScale the root's uniform scale, so the ground offset is in world units */
  constructor(voxelSize: number, worldScale = 1) {
    super({
      name: 'OctopusMaterial',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        // --- pose. Every one is read by OCTO_ARM_GLSL; a uniform added after the program
        // links is silently dead, so they are all declared here up front.
        uTime: { value: 0 },
        uLen: { value: 1.85 },
        uBend: { value: 1.55 },
        uWave: { value: 0.14 },
        // Integrated on the CPU, not uTime * a live rate: scaling absolute time means any
        // change to the speed jumps the phase by uTime * deltaSpeed, which grows without
        // bound, so a behaviour switch a minute in sweeps whole cycles per frame.
        uPhase: { value: 0 },
        uWaveFq: { value: 1.35 },
        uSplay: { value: 0 },
        uPitch: { value: -0.30 },
        uCrown: { value: 0 },
        uJet: { value: 0 },
        uOpen: { value: 0 },
        uCrawl: { value: 0 },
        uGait: { value: 1 },
        uBreath: { value: 0.5 },
        uMantleY: { value: 0.52 },
        uVox: { value: voxelSize },
        uDuneAmp: { value: OCEAN.duneAmplitude },
        uReefRise: { value: OCEAN.reefRise },
        uGround: { value: voxelSize * worldScale * 0.5 },

        // --- skin
        uChromT: { value: 0 },
        uContrast: { value: 0.74 },
        uSkinMode: { value: 1 },
        uPig: { value: new THREE.Color(0.30, 0.17, 0.47) },
        uPig2: { value: new THREE.Color(0.66, 0.56, 0.80) },

        // --- the scene's own lighting vocabulary
        uSunDir: {
          value: new THREE.Vector3(WATER.sunDir[0], WATER.sunDir[1], WATER.sunDir[2]).normalize(),
        },
        uSunColor: { value: srgb(WATER.sunColor) },
        uAmbientTop: { value: srgb(WATER.ambientTop) },
        uAmbientBottom: { value: srgb(WATER.ambientBottom) },
        uRimColor: { value: srgb(WATER.rimColor) },
        uFogColor: { value: srgb(WATER.fogColor) },
        uFogDensity: { value: WATER.fogDensity },
        uShadowMap: { value: null as THREE.Texture | null },
        uShadowMatrix: { value: new THREE.Matrix4() },
        uShadowStrength: { value: 0 },
      },
      side: THREE.FrontSide,
      transparent: false,
      fog: false,
    });
  }

  /** a depth twin for the shadow pass, sharing uniforms BY REFERENCE so the pose matches */
  makeShadowMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      name: 'OctopusMaterial:depth',
      vertexShader: VERT,
      fragmentShader: 'void main() { gl_FragColor = vec4(1.0); }',
      uniforms: this.uniforms,
      side: THREE.FrontSide,
      fog: false,
    });
  }

  /** the day cycle drives this material's lighting; see world/DayNight.ts */
  setEnv(env: SkyEnv): void {
    applyEnv(this.uniforms, env);
  }

  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    const u = this.uniforms;
    u.uShadowMap.value = map;
    (u.uShadowMatrix.value as THREE.Matrix4).copy(matrix);
    u.uShadowStrength.value = map === null ? 0 : strength;
  }
}
